import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { addDays } from "@/lib/calendars";
import { allowed, clampDay, dayOf, daysIn, isDay, localToday, monthGrid, partsOf, shiftMonths } from "@/lib/date-picker";
import { cn } from "@/lib/utils";

/**
 * A date picked from a month (ACL-577), in place of the browser's own date box. The field reads as the
 * record reads the date ("Oct 14, 2025"); pressing it opens the month with the day picked, today ringed
 * and the days outside `min` and `max` greyed, so a day that may not be saved cannot be picked. The month
 * and year at the top open a grid of years and then of months, so a birthday forty years back is three
 * presses, not five hundred.
 *
 * The keys are a calendar's: the arrows move a day or a week, Page Up and Page Down a month (with Shift a
 * year), Home and End to the week's ends, Enter picks and Escape closes. Picking a day closes it. Among
 * the months and the years the arrows move too, and each grid is one stop for Tab.
 */

type View = "days" | "months" | "years";

const FIRST_YEAR = 1900;
/** How far on the years run when nothing caps them: an expected close a few years out still fits. */
const YEARS_AHEAD = 30;

/** Every format reads a day as it is written: on UTC's clock, so no timezone moves it. */
const format = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" });
/** As the record reads a date (`record-fields.ts`), so the field does not change look when it opens. */
const SHORT = format({ year: "numeric", month: "short", day: "numeric" });
const LONG = format({ weekday: "long", year: "numeric", month: "long", day: "numeric" });
const MONTH_YEAR = format({ month: "long", year: "numeric" });
const MONTH = format({ month: "short" });
const LONG_MONTH = format({ month: "long" });
const WEEKDAY = format({ weekday: "short" });
const LONG_WEEKDAY = format({ weekday: "long" });

const at = (day: string) => {
  const { year, month, day: d } = partsOf(day);
  return new Date(Date.UTC(year, month - 1, d));
};
/** "Su" to "Sa", from a week that starts on a Sunday (2026-10-18). */
const WEEKDAYS = Array.from({ length: 7 }, (_, i) => addDays("2026-10-18", i));

/** How a day reads in the field: the record's own words, or what is stored when it is not a day. */
export function dayText(day: string): string {
  return isDay(day) ? SHORT.format(at(day)) : day;
}

export interface DatePickerProps {
  id?: string;
  /** `YYYY-MM-DD`, or "" for none. */
  value: string;
  onChange: (day: string) => void;
  /** The first and the last day that may be picked; the days outside are greyed. */
  min?: string;
  max?: string;
  /** What today is, ringed and offered as "Today": this computer's day unless given, or `max` when that
   *  comes first (a computer whose day has turned before the last day that may be picked). */
  today?: string;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  /** Whether Clear is offered: not for an answer that may not be emptied. */
  clearable?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** A press outside while it is open, which closes it. */
  onPointerDownOutside?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

export function DatePicker({
  id, value, onChange, min, max, today: givenToday, disabled, invalid, placeholder = "Pick a date",
  clearable = true, defaultOpen = false, onOpenChange, onPointerDownOutside, onKeyDown, className,
  "aria-label": ariaLabel, "aria-labelledby": labelledBy, "aria-describedby": describedBy,
}: DatePickerProps) {
  const computerDay = localToday();
  const today = givenToday ?? (max && computerDay > max ? max : computerDay);
  const picked = isDay(value) ? value : null;
  /** Where it opens: the day picked, else today, else the nearest day that may be picked. */
  const opening = () => clampDay(picked ?? today, min, max);
  const [open, setOpenState] = React.useState(defaultOpen);
  const [view, setView] = React.useState<View>("days");
  /** The day the keys move from; the month drawn is its month. */
  const [focus, setFocus] = React.useState(opening);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  /** Set when the keys or a press moved the focus, so the new day, month or year takes it once drawn. */
  const moved = React.useRef(false);

  const setOpen = (next: boolean) => {
    if (next) {
      setView("days");
      setFocus(opening());
    }
    setOpenState(next);
    onOpenChange?.(next);
  };
  const pick = (day: string) => {
    if (!allowed(day, min, max)) return;
    onChange(day);
    setOpen(false);
  };
  const go = (day: string, next: View = view) => {
    const to = clampDay(day, min, max);
    // An arrow at the last day that may be picked moves nothing, and the focus stays where it is.
    if (to === focus && next === view) return;
    moved.current = true;
    setFocus(to);
    setView(next);
  };
  React.useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    contentRef.current?.querySelector<HTMLElement>("[data-focus]")?.focus();
  }, [focus, view]);

  const { year, month } = partsOf(focus);
  const firstYear = min ? partsOf(min).year : FIRST_YEAR;
  const lastYear = max
    ? partsOf(max).year
    : Math.max(partsOf(today).year + YEARS_AHEAD, picked ? partsOf(picked).year : 0);
  // A step is offered while the month or year it lands on holds a day that may be picked.
  const step = view === "years" ? 0 : view === "months" ? 12 : 1;
  const before = shiftMonths(dayOf(year, month, 1), -step);
  const after = shiftMonths(dayOf(year, month, 1), step);
  const canBack = !min || lastOf(view === "months" ? dayOf(partsOf(before).year, 12, 1) : before) >= min;
  const canOn = !max || (view === "months" ? dayOf(partsOf(after).year, 1, 1) : after) <= max;

  const keys = (e: React.KeyboardEvent) => {
    const by: Record<string, () => string> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      PageUp: () => shiftMonths(focus, e.shiftKey ? -12 : -1),
      PageDown: () => shiftMonths(focus, e.shiftKey ? 12 : 1),
      Home: () => addDays(focus, -at(focus).getUTCDay()),
      End: () => addDays(focus, 6 - at(focus).getUTCDay()),
    };
    const next = by[e.key];
    if (!next) return;
    e.preventDefault();
    go(next());
  };
  /** The arrows among the months (three a row) and the years (four a row), within the years drawn. */
  const gridKeys = (e: React.KeyboardEvent, across: number) => {
    const by: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -across, ArrowDown: across };
    const n = by[e.key];
    if (n === undefined) return;
    e.preventDefault();
    const to = shiftMonths(focus, view === "months" ? n : n * 12);
    if (partsOf(to).year >= firstYear && partsOf(to).year <= lastYear) go(to);
  };

  const caption =
    view === "days"
      ? MONTH_YEAR.format(at(dayOf(year, month, 1)))
      : view === "months"
        ? String(year)
        : "Pick a year";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onKeyDown={onKeyDown}
          className={cn(
            "flex h-8 w-full items-center gap-2 rounded-md border bg-surface px-2.5 text-left text-sm shadow-xs",
            "transition-[border-color,box-shadow] duration-100",
            "focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted",
            invalid
              ? "border-danger-9 focus-visible:border-danger-9 focus-visible:ring-danger-9/25"
              : open
                ? "border-accent-8 ring-2 ring-accent-8/25"
                : "border-border hover:border-border-strong",
            className,
          )}
        >
          <CalendarDays className={cn("size-4 shrink-0", open ? "text-accent-11" : "text-text-subtle")} aria-hidden />
          <span className={cn("min-w-0 flex-1 truncate", value ? "text-text" : "text-text-subtle")}>
            {value ? dayText(value) : placeholder}
          </span>
          <ChevronDown
            className={cn("size-3.5 shrink-0 text-text-subtle transition-transform duration-150", open && "rotate-180")}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[18rem] p-3"
        aria-label="Pick a date"
        onOpenAutoFocus={(e) => {
          // The day it opens on takes the focus, so the arrows work at once.
          e.preventDefault();
          contentRef.current?.querySelector<HTMLElement>("[data-focus]")?.focus();
        }}
        // A press on its own field closes it as a toggle, not as a press elsewhere.
        onPointerDownOutside={(e) => {
          if (!triggerRef.current?.contains(e.target as Node)) onPointerDownOutside?.();
        }}
      >
        <div className="mb-2 flex items-center justify-between gap-1">
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-text",
              "hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
            )}
            aria-label={view === "years" ? "Back to the days" : `${caption}: pick a year`}
            onClick={() => go(focus, view === "years" ? "days" : "years")}
          >
            {caption}
            <ChevronDown
              className={cn("size-3.5 text-text-subtle transition-transform duration-150", view !== "days" && "rotate-180")}
              aria-hidden
            />
          </button>
          {view !== "years" && (
            <div className="flex items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={view === "months" ? "Previous year" : "Previous month"}
                disabled={!canBack}
                onClick={() => go(shiftMonths(focus, -step))}
              >
                <ChevronLeft />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={view === "months" ? "Next year" : "Next month"}
                disabled={!canOn}
                onClick={() => go(shiftMonths(focus, step))}
              >
                <ChevronRight />
              </Button>
            </div>
          )}
        </div>

        {/* One height for the days, the months and the years, so the panel does not jump as they change. */}
        <div className="h-[15.25rem]">
          {view === "days" && (
            <div role="grid" aria-label={caption} onKeyDown={keys}>
              <div role="row" className="grid grid-cols-7 pb-1">
                {WEEKDAYS.map((d) => (
                  <span
                    key={d}
                    role="columnheader"
                    aria-label={LONG_WEEKDAY.format(at(d))}
                    className="py-1 text-center text-2xs font-medium uppercase tracking-wide text-text-subtle"
                  >
                    {WEEKDAY.format(at(d)).slice(0, 2)}
                  </span>
                ))}
              </div>
              {chunks(monthGrid(year, month), 7).map((week) => (
                <div role="row" key={week[0]} className="grid grid-cols-7">
                  {week.map((d) => {
                    const ok = allowed(d, min, max);
                    const chosen = d === picked;
                    const inMonth = partsOf(d).month === month;
                    const why = ok
                      ? undefined
                      : max && d > max
                        ? "Later days cannot be picked"
                        : "Earlier days cannot be picked";
                    return (
                      <div role="gridcell" key={d} className="flex justify-center py-0.5" aria-selected={chosen}>
                        <button
                          type="button"
                          tabIndex={d === focus ? 0 : -1}
                          data-day={d}
                          data-focus={d === focus ? "" : undefined}
                          aria-label={LONG.format(at(d))}
                          aria-current={d === today ? "date" : undefined}
                          aria-disabled={!ok || undefined}
                          title={why}
                          onClick={() => pick(d)}
                          className={cn(
                            "flex size-8 items-center justify-center rounded-full text-sm tabular-nums transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8",
                            "focus-visible:ring-offset-1 focus-visible:ring-offset-surface-raised",
                            !ok
                              ? "cursor-not-allowed text-text-subtle opacity-40"
                              : chosen
                                ? "bg-accent-9 font-semibold text-white shadow-sm hover:bg-accent-10"
                                : d === today
                                  ? "font-semibold text-accent-11 ring-1 ring-inset ring-accent-7 hover:bg-accent-3"
                                  : inMonth
                                    ? "text-text hover:bg-accent-3 hover:text-accent-12"
                                    : "text-text-subtle hover:bg-surface-hover",
                          )}
                        >
                          {partsOf(d).day}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {view === "months" && (
            <div className="grid h-full grid-cols-3 grid-rows-4 gap-1.5 py-1" onKeyDown={(e) => gridKeys(e, 3)}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                const first = dayOf(year, m, 1);
                const ok = (!max || first <= max) && (!min || lastOf(first) >= min);
                const here = picked !== null && partsOf(picked).year === year && partsOf(picked).month === m;
                const now = partsOf(today).year === year && partsOf(today).month === m;
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={!ok}
                    tabIndex={m === month ? 0 : -1}
                    data-focus={m === month ? "" : undefined}
                    aria-label={`${LONG_MONTH.format(at(first))} ${year}`}
                    aria-pressed={here}
                    onClick={() => go(dayOf(year, m, Math.min(partsOf(focus).day, daysIn(year, m))), "days")}
                    className={monthOrYear(ok, here, now)}
                  >
                    {MONTH.format(at(first))}
                  </button>
                );
              })}
            </div>
          )}

          {view === "years" && (
            <YearGrid
              first={firstYear}
              last={lastYear}
              // A day stored before the first year drawn still leaves one year to focus.
              focused={Math.min(Math.max(year, firstYear), lastYear)}
              picked={picked ? partsOf(picked).year : null}
              current={partsOf(today).year}
              onKeyDown={(e) => gridKeys(e, 4)}
              onPick={(y) => go(dayOf(y, month, Math.min(partsOf(focus).day, daysIn(y, month))), "months")}
            />
          )}
        </div>

        <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-accent-11 hover:text-accent-12"
            disabled={!allowed(today, min, max)}
            onClick={() => pick(today)}
          >
            Today
            <span className="font-normal text-text-subtle">{SHORT.format(at(today))}</span>
          </Button>
          {clearable && value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The years, oldest first, scrolled so the one in view sits in the middle. */
function YearGrid({
  first, last, focused, picked, current, onPick, onKeyDown,
}: {
  first: number;
  last: number;
  focused: number;
  picked: number | null;
  current: number;
  onPick: (year: number) => void;
  onKeyDown: React.KeyboardEventHandler;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    ref.current?.querySelector<HTMLElement>("[data-focus]")?.scrollIntoView({ block: "center" });
  }, []);
  return (
    <div
      ref={ref}
      className="grid h-full auto-rows-[2.25rem] grid-cols-4 gap-1.5 overflow-y-auto py-1 pr-1"
      onKeyDown={onKeyDown}
    >
      {Array.from({ length: last - first + 1 }, (_, i) => first + i).map((y) => (
        <button
          key={y}
          type="button"
          tabIndex={y === focused ? 0 : -1}
          data-focus={y === focused ? "" : undefined}
          aria-pressed={y === picked}
          onClick={() => onPick(y)}
          className={monthOrYear(true, y === picked, y === current)}
        >
          {y}
        </button>
      ))}
    </div>
  );
}

/** A month's or a year's button: filled when it holds the day picked, ringed when it holds today. */
function monthOrYear(ok: boolean, picked: boolean, current: boolean): string {
  return cn(
    "h-full rounded-md text-sm tabular-nums transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8 focus-visible:ring-offset-1",
    "focus-visible:ring-offset-surface-raised",
    !ok
      ? "cursor-not-allowed text-text-subtle opacity-40"
      : picked
        ? "bg-accent-9 font-semibold text-white shadow-sm hover:bg-accent-10"
        : current
          ? "font-semibold text-accent-11 ring-1 ring-inset ring-accent-7 hover:bg-accent-3"
          : "text-text hover:bg-accent-3 hover:text-accent-12",
  );
}

/** The last day of a day's month. */
function lastOf(day: string): string {
  const { year, month } = partsOf(day);
  return dayOf(year, month, daysIn(year, month));
}

function chunks<T>(list: readonly T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(list.length / size) }, (_, i) => list.slice(i * size, i * size + size));
}
